import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Scale, FileCheck, Shield, Globe, BookOpen, FileText } from "lucide-react";
import { LegalSection } from "@/components/legal/LegalSection";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { LoadingState } from "@/components/ui/loading-state";
import { useEffect, useState } from "react";
import { Json } from "@/integrations/supabase/types";

type Document = {
  name: string;
  path: string;
  url: string;
};

type LegalSectionType = {
  id: string;
  title: string;
  status: string;
  content: string[];
  documents: Document[] | null;
  created_at: string;
};

const transformDocuments = (documents: Json[] | null): Document[] | null => {
  if (!documents) return null;
  return documents.map(doc => {
    if (typeof doc === 'object' && doc !== null) {
      return {
        name: (doc as any).name || '',
        path: (doc as any).path || '',
        url: (doc as any).url || ''
      };
    }
    return {
      name: '',
      path: '',
      url: ''
    };
  });
};

const SECTION_ICONS = {
  "Corporate Structure": Scale,
  "Regulatory Compliance": Shield,
  "Intellectual Property": FileCheck,
  "International Operations": Globe,
  "Key Agreements": FileText,
  "Internal Policies": BookOpen,
} as const;

const Legal = () => {
  const [sectionUrls, setSectionUrls] = useState<Record<string, string>>({});

  const { data: legalSections, isLoading } = useQuery({
    queryKey: ['legal-sections'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('legal_sections')
        .select('*')
        .order('title');
      
      if (error) throw error;
      
      return (data || []).map(section => ({
        ...section,
        documents: transformDocuments(section.documents)
      })) as LegalSectionType[];
    },
  });

  useEffect(() => {
    const loadDocumentUrls = async () => {
      const urls: Record<string, string> = {};
      if (legalSections) {
        for (const section of legalSections) {
          if (section.documents && section.documents.length > 0) {
            // Use the first document's URL if available
            urls[section.id] = section.documents[0].url;
          }
        }
        setSectionUrls(urls);
      }
    };

    loadDocumentUrls();
  }, [legalSections]);

  return (
    <div className="bg-muted/30">
      <div className="max-w-4xl mx-auto space-y-6">
        <Card>
          <CardHeader>
            <CardTitle className="text-xl text-primary flex items-center gap-2">
              <Scale className="h-6 w-6" />
              Legal and Compliance Overview
            </CardTitle>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <LoadingState />
            ) : (
              <div className="grid gap-6">
                {legalSections?.map((section) => (
                  <Card 
                    key={section.id}
                    className="hover:shadow-lg transition-all duration-300 group"
                  >
                    <LegalSection 
                      {...section}
                      icon={SECTION_ICONS[section.title as keyof typeof SECTION_ICONS] || FileText}
                      documentPath={sectionUrls[section.id]}
                    />
                  </Card>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Legal;